import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useContractMetrics, useContracts, useDeleteContract } from '../api/hooks';
import type { ContractListItemView } from '../api/types';
import {
  Badge,
  Button,
  DataTable,
  Modal,
  StatCard,
  Tabs,
  TextInput,
  useToast,
} from '../components/DesignSystem';
import type { DataTableColumn } from '../components/DesignSystem';
import { EyeIcon, PencilIcon, PlusIcon, TrashIcon } from '../components/icons';
import { PageHeader } from '../components/PageHeader';
import { errorMessage } from '../lib/errors';
import { formatDate, formatNumber } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { ContractDetailModal } from './authorised/ContractDetailModal';
import { EditApprovedTermModal } from './authorised/EditApprovedTermModal';
import { SecondaryDataCard } from './authorised/SecondaryDataCard';

type TabKey = 'all' | 'primary' | 'secondary';

type ModalState =
  | { kind: 'none' }
  | { kind: 'detail'; contract: ContractListItemView }
  | { kind: 'edit'; contract: ContractListItemView }
  | { kind: 'delete'; contract: ContractListItemView };

const inCatalogue = (total: number | undefined): string | undefined =>
  total === undefined ? undefined : `of ${formatNumber(total)} in catalogue`;

/** `/authorised-contracts` — the authorised catalogue plus secondary data. */
export function AuthorisedContractsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const [preselectedShowId, setPreselectedShowId] = useState<string | null>(null);
  const secondaryCardRef = useRef<HTMLElement>(null);

  // Search runs server-side (show name, season code, title, contract number,
  // counterparty); type filtering stays client-side on the fetched page so
  // tab switches are instant. Move it to a contractType param if a catalogue
  // ever exceeds the 100-row page.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const contracts = useContracts({
    status: 'authorized',
    search: debouncedSearch === '' ? undefined : debouncedSearch,
    limit: 100,
  });
  const metrics = useContractMetrics({ status: 'authorized' });
  const deleteContract = useDeleteContract();

  const allRows = useMemo(() => contracts.data?.data ?? [], [contracts.data]);

  const filteredRows = useMemo(
    () => (tab === 'all' ? allRows : allRows.filter((row) => row.contractType === tab)),
    [allRows, tab],
  );

  function scrollToSecondaryCard(showId: string | null) {
    setPreselectedShowId(showId);
    secondaryCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function handleDelete(contract: ContractListItemView) {
    try {
      await deleteContract.mutateAsync(contract.id);
      showToast('Contract deleted');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not delete contract'), 'error');
    }
  }

  const columns: DataTableColumn<ContractListItemView>[] = [
    { key: 'show', header: 'Show Name', render: (row) => row.showName ?? '—' },
    { key: 'season', header: 'Season', render: (row) => row.seasonCode ?? '—' },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <Badge tone={row.contractType === 'primary' ? 'primary' : 'teal'}>{row.contractType}</Badge>
      ),
    },
    {
      key: 'residuals',
      header: 'Residuals',
      align: 'center',
      render: (row) => (row.residualsCount === 0 ? 'None' : String(row.residualsCount)),
    },
    {
      key: 'parties',
      header: 'Parties',
      align: 'center',
      render: (row) => String(row.partiesCount),
    },
    { key: 'approved', header: 'Approved', render: (row) => formatDate(row.createdAt) },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <span className="rfp-icon-row">
          <button
            aria-label={`Add secondary data for ${row.title}`}
            className="rfp-iconbtn"
            onClick={() => scrollToSecondaryCard(row.showId)}
            title="Add secondary report data"
            type="button"
          >
            <PlusIcon />
          </button>
          <button
            aria-label={`View ${row.title}`}
            className="rfp-iconbtn"
            onClick={() => setModal({ kind: 'detail', contract: row })}
            title="View details"
            type="button"
          >
            <EyeIcon />
          </button>
          <button
            aria-label={`Edit ${row.title}`}
            className="rfp-iconbtn"
            onClick={() => setModal({ kind: 'edit', contract: row })}
            title="Edit approved term"
            type="button"
          >
            <PencilIcon />
          </button>
          <button
            aria-label={`Delete ${row.title}`}
            className="rfp-iconbtn rfp-iconbtn--danger"
            onClick={() => setModal({ kind: 'delete', contract: row })}
            title="Delete contract"
            type="button"
          >
            <TrashIcon />
          </button>
        </span>
      ),
    },
  ];

  // Metrics-driven, so the tab counts stay stable while a search narrows the table.
  const typeCounts = {
    all: metrics.data?.total ?? 0,
    primary: metrics.data?.countsByType.primary ?? 0,
    secondary: metrics.data?.countsByType.secondary ?? 0,
  };

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={() => navigate('/upload')} type="button">
            Upload Contract
          </Button>
        }
        caption="Every authorised contract in the catalogue, with residuals, parties, and secondary report data."
        title="Authorised Contracts & Reports"
      />

      <div className="rfp-toolbar">
        <Tabs
          active={tab}
          items={[
            { key: 'all', label: 'All', count: typeCounts.all },
            { key: 'primary', label: 'Primary', count: typeCounts.primary },
            { key: 'secondary', label: 'Secondary', count: typeCounts.secondary },
          ]}
          onChange={(key) => setTab(key as TabKey)}
        />
        <div className="rfp-toolbar__search">
          <TextInput
            aria-label="Search contracts"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by show name, season, or ID..."
            value={search}
          />
        </div>
      </div>

      <div className="rfp-stat-grid">
        <StatCard
          caption={inCatalogue(metrics.data?.catalogTotals.shows)}
          label="Authorised Shows"
          value={formatNumber(metrics.data?.distinctShows)}
        />
        <StatCard
          caption={inCatalogue(metrics.data?.catalogTotals.seasons)}
          label="Authorised Seasons"
          value={formatNumber(metrics.data?.distinctSeasons)}
        />
        <StatCard
          caption={inCatalogue(metrics.data?.catalogTotals.contracts)}
          label="Authorised Contracts"
          value={formatNumber(metrics.data?.total)}
        />
      </div>

      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Authorised Contracts ({filteredRows.length})</h2>
        </div>
        {contracts.isLoading ? (
          <p className="rfp-loading">Loading contracts…</p>
        ) : contracts.isError ? (
          <p className="rfp-inline-error" role="alert">
            Could not load contracts — please retry.
          </p>
        ) : (
          <DataTable
            columns={columns}
            emptyMessage={
              debouncedSearch === ''
                ? 'No authorised contracts yet — upload and authorise a contract first'
                : `No authorised contracts match “${debouncedSearch}”`
            }
            rowKey={(row) => row.id}
            rows={filteredRows}
          />
        )}
      </section>

      <SecondaryDataCard preselectedShowId={preselectedShowId} ref={secondaryCardRef} />

      {modal.kind === 'detail' && (
        <ContractDetailModal
          contract={modal.contract}
          onClose={() => setModal({ kind: 'none' })}
          seasonName={modal.contract.seasonCode ?? '—'}
          showName={modal.contract.showName ?? '—'}
        />
      )}

      {modal.kind === 'edit' && (
        <EditApprovedTermModal
          contract={modal.contract}
          onClose={() => setModal({ kind: 'none' })}
          seasonName={modal.contract.seasonCode ?? '—'}
          showName={modal.contract.showName ?? '—'}
        />
      )}

      {modal.kind === 'delete' && (
        <Modal
          actions={
            <>
              <Button onClick={() => handleDelete(modal.contract)} type="button">
                Delete
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Delete contract?"
        >
          <p>
            “{modal.contract.title}” and its shares, residuals, and simulations will be removed.
            This cannot be undone.
          </p>
        </Modal>
      )}
    </>
  );
}
